import { useMemo, useState, type ReactNode } from 'react';
import { DataError, useList } from '@hana/data';
import { useT } from '@hana/i18n';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { Button, Chip, Segmented, StateView, Table, useToast, type Column, type ScreenState } from '@hana/ui';
import { IconArrowLeft, IconBan, IconCalendarTime, IconChecklist, IconChevronDown, IconClock, IconDots, IconFileText, IconInfoCircle, IconLink, IconLock, IconMail, IconPlus, IconScale, IconShip, IconSparkles, IconTag, IconUser, IconUsers, type Icon } from '@tabler/icons-react';
import { useP2PAccess } from './access';
import { AgentModal, type AgentModalKind, type AgentModalState } from './AgentModal';
import { InquiryDrawer } from './InquiryDrawer';
import { INQUIRY_FILTERS, INQUIRY_STATUS, type InquiryStatus, INTERNAL_FILTERS, INTERNAL_STATUS, compareQuotes, inquiryStatus, ranked, replyCount } from './inquiry';
import { usd } from './format';
import type { AgentScore, Inquiry, InquiryAgent, InquiryQuote, InternalInquiry } from './types';
import './RateInquiryScreen.i18n';
import s from './inquiry.module.css';

type Tab = 'inq' | 'pr' | 'score';
const STATUS_LABEL: Record<InquiryStatus, string> = { draft: 'ri.sDraft', wait: 'ri.sWait', mine: 'ri.sMine', priced: 'ri.sPriced', won: 'ri.sWon', nores: 'ri.sNores' };
const LINKS: readonly { label: string; to: string; go: string }[] = [
  { label: 'ri.lRates', to: '/ezy/rates', go: 'ri.open' },
  { label: 'ri.lSvc', to: '/ezy/services', go: 'ri.open' },
  { label: 'ri.lP2p', to: '/ezy', go: 'ri.look' },
];

/** Thẻ gập được của chi tiết yêu cầu (board `a21`): nút "Mở hoặc gập <tên>", dòng tóm tắt khi gập. */
function Fold({ title, icon: Ico, info, badge, sum, open: open0 = true, flush, children }: { title: string; icon?: Icon; info?: string; badge?: ReactNode; sum: string; open?: boolean; flush?: boolean; children: ReactNode }) {
  const t = useT();
  const [open, setOpen] = useState(open0);
  return (
    <section className={s.fold} aria-label={title}>
      <div className={s.foldHead}>
        {Ico && <Ico size={18} className={s.foldIc} aria-hidden="true" />}
        <h2>{title}</h2>
        {info && <span role="img" className={s.foldInfo} aria-label={info} title={info}><IconInfoCircle size={14} aria-hidden="true" /></span>}
        {badge && <span className={s.n}>{badge}</span>}
        <button type="button" className={s.foldBtn} aria-expanded={open} aria-label={t('ri.fold', { v: title.toLowerCase() })} title={t('ri.foldTip')} onClick={() => setOpen((o) => !o)}>
          <IconChevronDown size={16} aria-hidden="true" style={{ transform: open ? 'rotate(180deg)' : undefined }} />
        </button>
      </div>
      {open ? <div className={`${s.foldBody} ${flush ? s.flush : ''}`}>{children}</div> : <div className={s.foldSum}>{sum}</div>}
    </section>
  );
}

const AGENT_STATE = {
  q: { label: 'ri.aQ', tone: 'info' as const },
  won: { label: 'ri.aWon', tone: 'ok' as const },
  n: { label: 'ri.aN', tone: 'neutral' as const },
  p: { label: 'ri.aP', tone: 'warn' as const },
};

interface Todo { k: string; sub: string; done?: boolean; muted?: boolean; act?: string; run?: () => void }

function Detail({ inq, agents, quotes, st }: { inq: Inquiry; agents: readonly InquiryAgent[]; quotes: ReadonlyMap<string, InquiryQuote> | null; st: InquiryStatus }) {
  const t = useT();
  const toast = useToast();
  const pending = () => toast({ msg: t('ri.noWrite'), tone: 'err' });
  const [md, setMd] = useState<AgentModalState | null>(null);
  const open = (kind: AgentModalKind, agent: InquiryAgent) => () => setMd({ kind, agent });
  const cmp = useMemo(() => (quotes ? compareQuotes(agents, [...quotes.values()]) : []), [agents, quotes]);
  const pendN = agents.filter((a) => a.status === 'p').length;
  const repN = agents.filter((a) => a.status !== 'p').length;
  const drafts = agents.filter((a) => a.draft);
  const cheapest = quotes ? agents.filter((a) => a.status === 'q' && !a.draft).sort((a, b) => (quotes.get(`${a.inquiry}|${a.idx}`)?.usd ?? Infinity) - (quotes.get(`${b.inquiry}|${b.idx}`)?.usd ?? Infinity))[0] : undefined;
  const sentN = inq.sent ? t('ri.sentN', { a: inq.sent, b: repN }) : t('ri.notSent');
  const log = inq.log ?? [[inq.created, t('ri.created', { v: inq.owner })]];
  const winner = inq.win !== undefined ? agents.find((a) => a.idx === inq.win) : undefined;

  const todo: Todo[] = [];
  if (st === 'draft') todo.push({ k: t('ri.todoSend'), sub: t('ri.todoSendSub'), act: t('ri.sendMore') });
  if (pendN > 0) todo.push({ k: t('ri.todoRemind', { n: pendN }), sub: t('ri.todoRemindSub'), act: t('ri.remind') });
  if (drafts.length) todo.push({ k: t('ri.todoDrafts', { n: drafts.length }), sub: drafts.map((a) => a.name).join(', '), act: t('ri.viewDraft'), run: drafts[0] && open('ai', drafts[0]) });
  if (cheapest && !winner) todo.push({ k: t('ri.pickPartner'), sub: t('ri.cheapestIs', { v: cheapest.name }), act: t('ri.pick'), run: open('win', cheapest) });
  if (winner) todo.push({ k: t('ri.picked'), sub: t('ri.pickedSub', { v: winner.name }), done: true });
  todo.push({ k: t('ri.scanMail'), sub: t('ri.scanSub', { v: inq.owner }), muted: true, act: t('ri.scan') });
  const todoN = todo.filter((k) => !k.done && !k.muted).length;

  const info: [string, string][] = [
    [t('ri.iId'), inq.id],
    [t('ri.iSvc'), inq.spec[0]?.[1] ?? inq.service],
    [t('ri.iOwner'), inq.owner],
    [t('ri.iCreated'), `${inq.created}/2026`],
    [t('ri.iFrom'), 'pricing@hanalogisticsvn.com'],
    [t('ri.iReply'), inq.sent ? t('ri.partnersN', { a: repN, b: inq.sent }) : t('ri.notSent')],
    [t('ri.iStatus'), t(STATUS_LABEL[st])],
  ];
  const pics = [
    { who: inq.pricing ?? 'TRANGHDD', role: t('ri.rolePricing') },
    { who: inq.owner, role: t('ri.roleSale') },
  ];

  const cols: Column<InquiryAgent>[] = [
    { key: 'name', header: t('ri.agent'), cell: (a) => (<><b>{a.name}</b><div className={s.sub}>{a.country}</div></>) },
    {
      key: 'st',
      header: t('ri.iStatus'),
      width: 142,
      cell: (a) => {
        const k = a.status === 'q' && winner?.idx === a.idx ? 'won' : a.status;
        return (
          <span className={s.inline}>
            <Chip tone={AGENT_STATE[k].tone}>{t(AGENT_STATE[k].label)}</Chip>
            {a.draft && <span title={t('ri.aiDraftTip')} className={s.aiDraft}><IconClock size={13} aria-hidden="true" />{t('ri.draftAi')}</span>}
          </span>
        );
      },
    },
    { key: 'when', header: t('ri.iReply'), width: 102, minor: true, cell: (a) => { const [d, r] = a.when.split(' · '); return (<span title={a.when} className={s.nw}>{d}{r && <div className={s.subIc} title={r}><IconClock size={13} aria-hidden="true" />{r}</div>}</span>); } },
    {
      key: 'info',
      header: t('ri.hInfo'),
      width: 205,
      minor: true,
      cell: (a) => {
        const [car, tt, to] = a.info.split(' · ');
        if (!car || !tt || !to) return <span title={a.info}>{a.info}</span>;
        return (
          <span className={s.infoIc} title={a.info}>
            <span><IconShip size={13} aria-hidden="true" />{car}</span>
            <span><IconClock size={13} aria-hidden="true" />{tt}</span>
            <span><IconCalendarTime size={13} aria-hidden="true" />{to.replace(/^đến /, '')}</span>
          </span>
        );
      },
    },
    ...(quotes
      ? ([
          {
            key: 'price',
            header: t('ri.hAllin'),
            width: 150,
            align: 'right',
            cell: (a) => {
              if (a.status !== 'q') return '–';
              const q = quotes.get(`${a.inquiry}|${a.idx}`);
              return q ? (<>{usd(q.usd)}<div className={s.sub}>{q.orig}</div></>) : '–';
            },
          },
        ] as Column<InquiryAgent>[])
      : []),
    {
      key: 'act',
      header: t('ri.actions'),
      width: 191,
      cell: (a) => (
        <span className={s.acts}>
          {a.draft && <Button size="sm" variant="ask" icon={<IconSparkles size={14} aria-hidden="true" />} onClick={open('ai', a)}>{t('ri.viewAi')}</Button>}
          {a.status === 'p' && <Button size="sm" onClick={open('price', a)}>{t('ri.savePrice')}</Button>}
          {a.status === 'p' && <Button size="sm" aria-label={t('ri.noQuote')} title={t('ri.noQuote')} icon={<IconBan size={14} aria-hidden="true" />} onClick={pending} />}
          {a.status !== 'p' && <Button size="sm" aria-label={t('ri.mail')} title={t('ri.mail')} icon={<IconMail size={14} aria-hidden="true" />} onClick={open('thread', a)} />}
          {quotes && a.status === 'q' && !a.draft && !winner && <Button size="sm" variant="primary" onClick={open('win', a)}>{t('ri.pick')}</Button>}
        </span>
      ),
    },
  ];
  const showCmp = quotes !== null && cmp.length > 0;
  const lane = inq.spec.map(([, v]) => v).join(' · ');

  return (
    <div className={s.d21}>
      <div className={s.d21L}>
        <Fold title={t('ri.request')} icon={IconFileText} info={t('ri.fiReq')} badge={inq.service} sum={lane}>
          <dl className={s.spec}>
            {inq.spec.map(([k, v]) => (
              <div key={k}>
                <dt>{k}</dt>
                <dd>{v}</dd>
              </div>
            ))}
          </dl>
        </Fold>
        <Fold flush title={t('ri.partners')} icon={IconUsers} info={quotes === null ? t('ri.fiPartnersNo') : t('ri.fiPartners')} badge={sentN} sum={sentN}>
          {agents.length ? <Table label={t('ri.partners')} columns={cols} rows={agents} rowKey={(a) => `${a.inquiry}|${a.idx}`} dense /> : <p className={s.muted}>{t('ri.noneSent')}</p>}
          {quotes === null && (
            <div className={s.note} role="note">
              <IconLock size={16} aria-hidden="true" />
              {t('ri.masked')}
            </div>
          )}
        </Fold>
        {showCmp && (
          <Fold title={t('ri.compare')} icon={IconScale} info={t('ri.fiCompare')} sum={t('ri.compareSum', { n: cmp.length, v: cmp[0]!.usd })}>
            <div className={s.cmp}>
              {cmp.map((r) => (
                <div key={r.name} className={s.cmpRow}>
                  <span title={r.name}>{r.name}</span>
                  <span className={s.bar}><span className={r.cheapest ? s.barBest : undefined} style={{ width: `${r.bar}%` }} /></span>
                  <b>{usd(r.usd)}</b>
                  <span className={s.sub}>{r.cheapest ? t('ri.cheapest') : `+${r.deltaPct}%`}</span>
                </div>
              ))}
            </div>
          </Fold>
        )}
        <Fold title={t('ri.activity')} icon={IconClock} info={t('ri.fiActivity')} badge={String(log.length)} sum={log[log.length - 1]?.[1] ?? ''}>
          <ul className={s.log}>
            {log.map(([at, k]) => (
              <li key={`${at}${k}`}>
                <span className={s.sub}>{at}</span>
                <span>{k}</span>
              </li>
            ))}
          </ul>
        </Fold>
      </div>
      <div className={s.d21R}>
        <Fold title={t('ri.todo')} icon={IconChecklist} info={t('ri.fiTodo')} badge={todoN ? t('ri.todoBadge', { n: todoN }) : t('ri.ok')} sum={todoN ? t('ri.todoSum', { n: todoN }) : t('ri.noUrgent')}>
          <ul className={s.todo}>
            {todo.map((k) => (
              <li key={k.k}>
                <span className={`${s.tick} ${k.done ? s.tickOk : k.muted ? s.tickMute : s.tickWarn}`} role="img" aria-label={k.done ? t('ri.done') : t('ri.needed')}>{k.done ? '✓' : ''}</span>
                <span className={s.todoTx}>
                  <b>{k.k}</b>
                  <span className={s.sub}>{k.sub}</span>
                </span>
                {k.act && <Button size="sm" onClick={k.run ?? pending}>{k.act}</Button>}
              </li>
            ))}
          </ul>
        </Fold>
        <Fold title={t('ri.mainInfo')} icon={IconTag} info={t('ri.fiMain')} sum={`${inq.id} · ${inq.owner} · ${t(STATUS_LABEL[st])}`}>
          <dl className={s.info}>
            {info.map(([k, v]) => (
              <div key={k}>
                <dt>{k}</dt>
                <dd title={v}>{v}</dd>
              </div>
            ))}
          </dl>
        </Fold>
        <Fold title={t('ri.owners')} icon={IconUser} info={t('ri.fiOwners')} sum={pics.map((p) => p.who).join(' · ')}>
          <ul className={s.pics}>
            {pics.map((p) => (
              <li key={p.role} title={p.role}>
                <span className={s.av} aria-hidden="true">{p.who.slice(0, 2).toUpperCase()}</span>
                <span className={s.todoTx}>
                  <b>{p.who}</b>
                  <span className={s.sub}>{p.role}</span>
                </span>
              </li>
            ))}
          </ul>
        </Fold>
        <Fold title={t('ri.links')} icon={IconLink} info={t('ri.fiLinks')} open={false} sum={t('ri.linksSum', { n: LINKS.length })}>
          <ul className={s.links}>
            {LINKS.map((l) => (
              <li key={l.to}>
                <a href={l.to}>
                  <span>{t(l.label)}</span>
                  <span className={s.sub}>{t(l.go)}</span>
                </a>
              </li>
            ))}
          </ul>
        </Fold>
      </div>
      <AgentModal state={md} inquiry={inq.id} created={inq.created} subject={`RATE INQUIRY · ${lane} · HANA LOGISTICS`} quote={md ? quotes?.get(`${md.agent.inquiry}|${md.agent.idx}`) : undefined} onClose={() => setMd(null)} />
    </div>
  );
}

function InquiryList({ rows, filter, setFilter, onOpen }: { rows: { q: Inquiry; st: InquiryStatus; rc: ReturnType<typeof replyCount> }[]; filter: (typeof INQUIRY_FILTERS)[number]['key']; setFilter: (k: (typeof INQUIRY_FILTERS)[number]['key']) => void; onOpen: (id: string) => void }) {
  const t = useT();
  const shown = rows.filter((r) => filter === 'all' || r.st === filter);
  return (
    <section className={s.listWrap} aria-label={t('ri.list')}>
      <div className={s.chips} role="group" aria-label={t('ri.filter')}>
        {INQUIRY_FILTERS.map((f) => (
          <button key={f.key} type="button" className={s.chipBtn} aria-pressed={filter === f.key} onClick={() => setFilter(f.key)}>
            {f.label} <span>{f.key === 'all' ? rows.length : rows.filter((r) => r.st === f.key).length}</span>
          </button>
        ))}
      </div>
      <div className={s.list}>
        {shown.map(({ q, st, rc }) => (
          <button key={q.id} type="button" className={s.item} onClick={() => onOpen(q.id)}>
            <span className={s.itemTop}>
              <span className={s.code}>{q.id}</span>
              <Chip tone={INQUIRY_STATUS[st].tone}>{INQUIRY_STATUS[st].label}</Chip>
            </span>
            <b>{q.title}</b>
            <span className={s.sub}>{q.owner} · {q.created} · {q.service}</span>
            <span className={s.prog}>
              <span className={s.bar}><span style={{ width: `${rc.pct}%` }} /></span>
              <span className={s.sub}>{q.sent ? t('ri.repliedN', { a: rc.replied, b: rc.sent }) : t('ri.notSent')}</span>
            </span>
          </button>
        ))}
        {shown.length === 0 && <p className={s.muted}>{t('ri.noneInGroup')}</p>}
      </div>
    </section>
  );
}

function DetailWithQuotes(props: { inq: Inquiry; agents: readonly InquiryAgent[]; st: InquiryStatus }) {
  const quotes = useList('inquiryQuote');
  const map = useMemo(() => new Map((quotes.data?.items ?? []).map((q) => [q.id, q])), [quotes.data]);
  return <Detail {...props} quotes={map} />;
}

function InternalTab({ rows }: { rows: readonly InternalInquiry[] }) {
  const t = useT();
  const [f, setF] = useState<(typeof INTERNAL_FILTERS)[number]['key']>('open');
  const test = INTERNAL_FILTERS.find((x) => x.key === f)!.test;
  const shown = rows.filter(test);
  const cols: Column<InternalInquiry>[] = [
    { key: 'id', header: t('ri.ticket'), width: 200, cell: (r) => (<><span className={s.code}>{r.id}</span>{r.note && <div className={s.sub}>{r.note}</div>}</>) },
    { key: 'lane', header: t('ri.lane'), cell: (r) => r.lane },
    { key: 'car', header: t('ri.carrier'), width: 80, cell: (r) => <Chip>{r.carrier}</Chip> },
    { key: 'who', header: t('ri.askedBy'), width: 190, minor: true, cell: (r) => r.who },
    { key: 'at', header: t('ri.askedAt'), width: 120, minor: true, cell: (r) => r.at },
    { key: 'st', header: t('ri.iStatus'), width: 120, cell: (r) => <Chip tone={r.status === 'Quoted' ? 'ok' : r.status === 'Skip' ? 'neutral' : 'info'}>{INTERNAL_STATUS[r.status]}</Chip> },
    { key: 'res', header: t('ri.result'), minor: true, cell: (r) => r.result || '–' },
  ];
  return (
    <section className={s.card} aria-label={t('ri.internal')}>
      <div className={s.chips} role="group" aria-label={t('ri.filterTickets')}>
        {INTERNAL_FILTERS.map((x) => (
          <button key={x.key} type="button" className={s.chipBtn} aria-pressed={f === x.key} onClick={() => setF(x.key)}>
            {x.label} <span>{rows.filter(x.test).length}</span>
          </button>
        ))}
      </div>
      <Table label={t('ri.internalTickets')} columns={cols} rows={shown} rowKey={(r) => r.id} dense />
    </section>
  );
}

function ScoreTab({ scores }: { scores: readonly AgentScore[] }) {
  const t = useT();
  const rk = ranked(scores);
  const recv = scores.reduce((n, x) => n + x.received, 0);
  const won = scores.reduce((n, x) => n + x.won, 0);
  const kpi = [
    { k: t('ri.agent'), v: String(scores.length), d: t('ri.d90') },
    { k: t('ri.kRecv'), v: String(recv), d: t('ri.kRecvD') },
    { k: t('ri.kRate'), v: `${scores.length ? Math.round(scores.reduce((n, x) => n + x.replyRate, 0) / scores.length) : 0}%`, d: t('ri.kRateD') },
    { k: t('ri.aWon'), v: `${won} / ${recv}`, d: recv ? `${Math.round((won / recv) * 100)}%` : '–' },
  ];
  const cols: Column<(typeof rk)[number]>[] = [
    { key: 'name', header: 'Đại lý / đối tác', cell: (r) => (<><b>{r.name}</b><div className={s.sub}>{r.country}</div></>) },
    { key: 'n', header: t('ri.recv'), width: 80, align: 'right', cell: (r) => r.received },
    { key: 'rr', header: t('ri.iReply'), width: 90, align: 'right', cell: (r) => `${r.replyRate}%` },
    { key: 'tt', header: t('ri.avgReply'), width: 130, align: 'right', minor: true, cell: (r) => r.avgReply },
    { key: 'ch', header: t('ri.cheapestCol'), width: 90, align: 'right', minor: true, cell: (r) => r.cheapest },
    { key: 'win', header: t('ri.aWon'), width: 100, align: 'right', minor: true, cell: (r) => r.won },
    {
      key: 'pt',
      header: t('ri.points'),
      width: 140,
      cell: (r) => (
        <span className={s.prog}>
          <span className={s.bar}><span className={r.points >= 60 ? s.barBest : undefined} style={{ width: `${r.points}%` }} /></span>
          <b>{r.points}</b>
        </span>
      ),
    },
  ];
  return (
    <>
      <div className={s.kpis}>
        {kpi.map((k) => (
          <div key={k.k} className={s.kpi}>
            <span>{k.k}</span>
            <b>{k.v}</b>
            <small>{k.d}</small>
          </div>
        ))}
      </div>
      <section className={s.card} aria-label={t('ri.scoreboard')}>
        <h2>{t('ri.scoreboardH')}</h2>
        <Table label={t('ri.scoreboard')} columns={cols} rows={rk} rowKey={(r) => r.name} dense />
      </section>
    </>
  );
}

export function RateInquiryScreen() {
  const t = useT();
  const access = useP2PAccess();
  const toast = useToast();
  const inq = useList('inquiry');
  const ag = useList('inquiryAgent');
  const pr = useList('internalInquiry');
  const sc = useList('agentScore');
  const [tab, setTab] = useState<Tab>('inq');
  const [filter, setFilter] = useState<(typeof INQUIRY_FILTERS)[number]['key']>('all');
  const [sel, setSel] = useState<string | null>(null);
  const [dw, setDw] = useState(false);

  const inquiries = (inq.data?.items ?? []) as Inquiry[];
  const agents = (ag.data?.items ?? []) as InquiryAgent[];
  const prs = (pr.data?.items ?? []) as InternalInquiry[];
  const scores = (sc.data?.items ?? []) as AgentScore[];
  const agentsOf = (id: string) => agents.filter((a) => a.inquiry === id);
  const rows = inquiries.map((q) => ({ q, st: inquiryStatus(q, agentsOf(q.id)), rc: replyCount(q, agentsOf(q.id)) }));
  const openInq = rows.filter((r) => r.st === 'wait' || r.st === 'mine').length;
  const openPr = prs.filter((r) => r.status === 'Asked' || r.status === 'Taken').length;
  const cur = tab === 'inq' && sel ? rows.find((r) => r.q.id === sel) : undefined;

  const failed = inq.error ?? ag.error ?? pr.error ?? sc.error;
  let state: ScreenState = 'ready';
  if (access.force) state = access.force;
  else if (inq.isPending || ag.isPending || pr.isPending || sc.isPending) state = 'loading';
  else if (failed) state = failed instanceof DataError && failed.code === 'noperm' ? 'noperm' : 'error';
  else if (!inquiries.length) state = 'empty';

  const pending = () => toast({ msg: t('ri.noWrite'), tone: 'err' });
  const detail = state === 'ready' && cur;
  const pendN = cur ? agentsOf(cur.q.id).filter((a) => a.status === 'p').length : 0;
  const priK = pendN > 0 ? t('ri.remindN', { n: pendN }) : cur?.st === 'draft' ? t('ri.sendMorePartners') : t('ri.scanMail');

  return (
    <>
      <Page kind="table">
      {detail ? (
        <PageHeader
          title={cur.q.title}
          lead={
            <span className={s.lead}>
              <Button variant="ghost" aria-label={t('ri.back')} title={t('ri.back')} icon={<IconArrowLeft size={18} aria-hidden="true" />} onClick={() => setSel(null)} />
              <span className={s.code}>{cur.q.id}</span>
            </span>
          }
          meta={<Chip tone={INQUIRY_STATUS[cur.st].tone}>{t(STATUS_LABEL[cur.st])}</Chip>}
          actions={
            <>
              <HeaderTool label={t('ri.tasks')} icon={IconChecklist} />
              <HeaderTool label={t('ri.reqActions')} icon={IconDots} />
              <Button variant="primary" onClick={cur?.st === 'draft' ? () => setDw(true) : pending}>{priK}</Button>
            </>
          }
        />
      ) : (
        <PageHeader
          title={t('ri.title')}
          tabs={
            <Segmented
              label={t('ri.section')}
              value={tab}
              onChange={(k) => { setTab(k); setSel(null); }}
              items={[{ key: 'inq', label: t('ri.tInq'), n: openInq || undefined }, { key: 'pr', label: t('ri.internal'), n: openPr || undefined }, { key: 'score', label: t('ri.tScore') }]}
            />
          }
          actions={
            <>
              <HeaderTool label={t('ri.tasks')} icon={IconChecklist} />
              <HeaderTool label={t('ri.more')} icon={IconDots} />
              <Button variant="primary" icon={tab === 'pr' ? undefined : <IconPlus size={16} aria-hidden="true" />} onClick={tab === 'pr' ? () => undefined : () => setDw(true)}>
                {tab === 'pr' ? t('ri.viewOpen') : t('ri.create')}
              </Button>
            </>
          }
        />
      )}
      <div className={s.page}>
        <StateView
          state={state}
          title={state === 'empty' ? t('ri.emptyT') : state === 'noperm' ? t('ri.nopermT') : state === 'error' ? t('ri.errT') : undefined}
          desc={state === 'empty' ? t('ri.emptyD') : state === 'error' ? t('ri.errD') : undefined}
        >
          {tab === 'inq' ? (
            cur ? (
              access.money ? <DetailWithQuotes inq={cur.q} agents={agentsOf(cur.q.id)} st={cur.st} /> : <Detail inq={cur.q} agents={agentsOf(cur.q.id)} quotes={null} st={cur.st} />
            ) : (
              <InquiryList rows={rows} filter={filter} setFilter={setFilter} onOpen={setSel} />
            )
          ) : tab === 'pr' ? (
            <InternalTab rows={prs} />
          ) : (
            <ScoreTab scores={scores} />
          )}
        </StateView>
      </div>
      </Page>
      <InquiryDrawer open={dw} onClose={() => setDw(false)} />
    </>
  );
}
